import { createContext, useContext, useState } from "react";
import {
  canAddNewPaymentItem,
  type PaymentFormItem as PaymentItem,
} from "./serialization";
import type { NodeData } from "@/workflow_api/types/workflow";

const PaymentNodeContext = createContext<{
  payment_title: string;
  hasPaymentTitleChange: boolean;
  setPaymentTitle: (title: string) => void;
  description: string;
  setDescription: (description: string) => void;
  canEximp: boolean;
  setCanEximp: (canEximp: boolean) => void;
  express: boolean;
  setExpress: (express: boolean) => void;
  paymentItems: PaymentItem[];
  addPaymentItem: () => void;
  updatePaymentItem: (id: string, updates: Partial<PaymentItem>) => void;
  removePaymentItem: (id: string) => void;
  setValidationEnabled: (enabled: boolean) => void;
  validationEnabled: boolean;
  isItemValid: (item: PaymentItem) => {
    hasValidConcept: boolean;
    hasValidAmount: boolean;
    isValid: boolean;
  };
  canAddNewItem: () => boolean;
}>({
  payment_title: "",
  hasPaymentTitleChange: false,
  setPaymentTitle: () => {},
  description: "",
  setDescription: () => {},
  canEximp: false,
  setCanEximp: () => {},
  express: false,
  setExpress: () => {},
  addPaymentItem: () => {},
  paymentItems: [],
  updatePaymentItem: () => {},
  removePaymentItem: () => {},
  setValidationEnabled: () => {},
  validationEnabled: false,
  isItemValid: () => {
    return { hasValidConcept: false, hasValidAmount: false, isValid: false };
  },
  canAddNewItem: () => false,
});

export const PaymentNodeProvider = ({
  children,
  data,
}: {
  children: React.ReactNode;
  data: NodeData;
}) => {
  const hasInputs = data?.inputs?.length > 0;

  const [hasPaymentTitleChange, setHasPaymentTitleChange] =
    useState<boolean>(false);

  const [payment_title, setPaymentTitle] = useState<string>(() => {
    if (hasInputs) {
      const titleInput = data.inputs.find((input) => input.name === "title");
      if (titleInput) {
        return titleInput.value as string;
      }
    }
    return "";
  });

  const onChangeTitle = (title: string) => {
    if (!hasPaymentTitleChange) {
      setHasPaymentTitleChange(true);
    }
    setPaymentTitle(title);
  };

  const [description, setDescription] = useState<string>(() => {
    if (hasInputs) {
      const descriptionInput = data.inputs.find(
        (input) => input.name === "description",
      );
      if (descriptionInput) {
        return descriptionInput.value as string;
      }
    }
    return "";
  });
  const [canEximp, setCanEximp] = useState<boolean>(() => {
    if (hasInputs) {
      const mandatoryInput = data.inputs.find(
        (input) => input.name === "is_mandatory",
      );
      if (mandatoryInput) {
        return mandatoryInput.value as boolean;
      }
    }
    return false;
  });
  const [express, setExpress] = useState<boolean>(() => {
    if (hasInputs) {
      const expressInput = data.inputs.find(
        (input) => input.name === "is_express",
      );
      if (expressInput) {
        return expressInput.value as boolean;
      }
    }
    return false;
  });
  const [paymentItems, setPaymentItems] = useState<PaymentItem[]>(() => {
    if (hasInputs) {
      const items: PaymentItem[] = [];
      // Assuming inputs are structured in pairs of concept and value
      const conceptInputs = data.inputs.filter((input) =>
        input.name.startsWith("concepts_"),
      );

      conceptInputs.forEach((conceptInput) => {
        const index = conceptInput.name.split("_")[1];
        const valueInput = data.inputs.find(
          (input) => input.name === `values_${index}`,
        );

        const concept: {
          concept: string;
          conceptType: "input" | "variable";
          conceptNode?: string;
          conceptField?: string;
          conceptOutput?: string;
          conceptGlobalType?: {
            value: string;
            type:
              | "APPLICANT"
              | "NODE_EXECUTION_START"
              | "NODE_EXECUTION_FINISH"
              | "CURRENT_TIME";
          };
        } = {
          concept: "",
          conceptType: "input",
          conceptNode: undefined,
          conceptField: undefined,
          conceptOutput: undefined,
          conceptGlobalType: undefined,
        };
        const amount: {
          amount: string;
          amountType: "input" | "variable";
          amountNode?: string;
          amountField?: string;
          amountOutput?: string;
          amountGlobalType?: {
            value: string;
            type:
              | "APPLICANT"
              | "NODE_EXECUTION_START"
              | "NODE_EXECUTION_FINISH"
              | "CURRENT_TIME";
          };
        } = {
          amount: "",
          amountType: "input",
          amountNode: undefined,
          amountField: undefined,
          amountOutput: undefined,
          amountGlobalType: undefined,
        };
        if (valueInput) {
          const isConceptInput =
            conceptInput.value !== null && !conceptInput.global_type;
          const isValueInput =
            valueInput.value !== null && !valueInput.global_type;

          if (isConceptInput) {
            concept.concept = conceptInput.value as string;
            concept.conceptType = "input";
          } else {
            const source = conceptInput.source_connections?.[0];
            concept.concept = "";
            concept.conceptType = "variable";
            // Use source_connections if available, otherwise fallback to connection/field properties
            concept.conceptNode =
              source?.source_node_id ||
              (conceptInput.connection as string) ||
              undefined;
            concept.conceptField =
              source?.field_id || (conceptInput.field as string) || undefined;
            concept.conceptOutput = source?.output_name || undefined;
            if (conceptInput.global_type) {
              concept.conceptGlobalType = {
                value: conceptInput.value as string,
                type: conceptInput.global_type,
              };
            }
          }
          if (isValueInput) {
            amount.amount = String(Number(valueInput.value).toFixed(2));
            amount.amountType = "input";
          } else {
            const source = valueInput.source_connections?.[0];
            amount.amount = "";
            amount.amountType = "variable";
            // Use source_connections if available, otherwise fallback to connection/field properties
            amount.amountNode =
              source?.source_node_id ||
              (valueInput.connection as string) ||
              undefined;
            amount.amountField =
              source?.field_id || (valueInput.field as string) || undefined;
            amount.amountOutput = source?.output_name || undefined;
            if (valueInput.global_type) {
              amount.amountGlobalType = {
                value: valueInput.value as string,
                type: valueInput.global_type,
              };
            }
          }
          items.push({
            id: index,
            ...concept,
            ...amount,
            hasChange: false,
          });
        }
      });
      if (items.length > 0) {
        return items;
      }
    }
    return [
      {
        id: Date.now().toString(),
        concept: "",
        amount: "",
        conceptType: "input",
        amountType: "input",
        hasChange: false,
      },
    ];
  });
  const [validationEnabled, setValidationEnabled] = useState<boolean>(false);

  const canAddNewItem = (): boolean => {
    return canAddNewPaymentItem(paymentItems);
  };

  const addPaymentItem = () => {
    if (!canAddNewItem()) return;

    const newItem: PaymentItem = {
      id: Date.now().toString(),
      concept: "",
      amount: "",
      conceptType: "input",
      amountType: "input",
      hasChange: false,
    };
    setPaymentItems([...paymentItems, newItem]);
  };
  const updatePaymentItem = (id: string, updates: Partial<PaymentItem>) => {
    // Activar validaciones cuando el usuario interactúe
    if (!validationEnabled) {
      setValidationEnabled(true);
    }
    setPaymentItems((items) =>
      items.map((item) =>
        item.id === id ? { ...item, ...updates, hasChange: true } : item,
      ),
    );
  };

  const removePaymentItem = (id: string) => {
    if (paymentItems.length > 1) {
      setPaymentItems((items) => items.filter((item) => item.id !== id));
    }
  };

  const isItemValid = (item: PaymentItem) => {
    const hasValidConcept =
      item.conceptType === "input"
        ? item.concept.trim() !== ""
        : !!(
            item.conceptNode ||
            item.conceptField ||
            item.conceptOutput ||
            item.conceptGlobalType
          );

    const hasValidAmount =
      item.amountType === "input"
        ? item.amount.trim() !== "" &&
          !isNaN(parseFloat(item.amount)) &&
          parseFloat(item.amount) > 0
        : !!(
            item.amountNode ||
            item.amountField ||
            item.amountOutput ||
            item.amountGlobalType
          );

    return {
      hasValidConcept,
      hasValidAmount,
      isValid: hasValidConcept && hasValidAmount,
    };
  };

  return (
    <PaymentNodeContext.Provider
      value={{
        hasPaymentTitleChange,
        payment_title,
        setPaymentTitle: onChangeTitle,
        description,
        setDescription,
        canEximp,
        setCanEximp,
        express,
        setExpress,
        addPaymentItem,
        updatePaymentItem,
        paymentItems,
        setValidationEnabled,
        validationEnabled,
        removePaymentItem,
        isItemValid,
        canAddNewItem,
      }}
    >
      {children}
    </PaymentNodeContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const usePaymentNodeContext = () => {
  const context = useContext(PaymentNodeContext);
  if (context === undefined) {
    throw new Error(
      "usePaymentNodeContext must be used within a PaymentNodeProvider",
    );
  }
  return context;
};
